import React, { useMemo } from "react";
import { continents, countries } from "countries-list";
import Search from "../../components/commons/Search";
import "./GroupMultiSelect.css";

let countryNameCodeMap = {};
Object.keys(countries).forEach((key) => {
  countryNameCodeMap[key] = countries[key].name;
});

const GroupMultiSelect = ({
  type = "region",
  allGroups = [],
  selectedOptions = [],
  onChange,
  label,
  placeholder,
}) => {
  const formattedGroups = useMemo(() => {
    // Use Map to deduplicate by group id
    const uniqueGroupsMap = new Map();
    allGroups.forEach((group) => {
      if (group.id && !uniqueGroupsMap.has(group.id)) {
        uniqueGroupsMap.set(group.id, {
          label: group.groupName,
          value: group.id,
        });
      }
    });
    return Array.from(uniqueGroupsMap.values());
  }, [allGroups]);

  const supportersRegions = useMemo(() => {
    const uniqueRegions = Array.from(
      new Set(allGroups.map((group) => group.supportersRegion).filter(Boolean))
    ).map((code) => ({
      label: continents[code] || code,
      value: code,
    }));
    return uniqueRegions;
  }, [allGroups]);

  const supportersCountries = useMemo(() => {
    const uniqueCountries = Array.from(
      new Set(allGroups.map((group) => group.supportersCountry).filter(Boolean))
    ).map((code) => ({
      label: countryNameCodeMap[code] || code,
      value: code,
    }));
    return uniqueCountries;
  }, [allGroups]);

  const allOptionsMap = useMemo(() => {
    const map = new Map();
    formattedGroups.forEach((opt) => map.set(opt.value, opt));
    supportersRegions.forEach((opt) => map.set(opt.value, opt));
    supportersCountries.forEach((opt) => map.set(opt.value, opt));
    return map;
  }, [formattedGroups, supportersRegions, supportersCountries]);

  const selectedOptionsForSearch = useMemo(() => {
    return selectedOptions
      .map((value) => allOptionsMap.get(value) || { label: value, value })
      .filter(Boolean);
  }, [selectedOptions, allOptionsMap]);

  const handleSearchChange = (options) => {
    const values = options.map((opt) => opt.value);
    onChange(values);
  };

  const count = useMemo(() => {
    if (type === "group") {
      return `${selectedOptions.length} group(s) selected`;
    }

    const regionValueSet = new Set(supportersRegions.map((r) => r.value));
    const countryValueSet = new Set(supportersCountries.map((c) => c.value));

    // Use Set to track unique group IDs to avoid counting duplicates
    const uniqueGroupIds = new Set();

    selectedOptions.forEach((value) => {
      if (regionValueSet.has(value)) {
        allGroups
          .filter((g) => g.supportersRegion === value)
          .forEach((g) => {
            if (g.id) {
              uniqueGroupIds.add(g.id);
            }
          });
      } else if (countryValueSet.has(value)) {
        allGroups
          .filter((g) => g.supportersCountry === value)
          .forEach((g) => {
            if (g.id) {
              uniqueGroupIds.add(g.id);
            }
          });
      }
    });

    const totalGroups = uniqueGroupIds.size;

    if (type === "country") {
      return `${selectedOptions.length} country/countries selected (${totalGroups} groups)`;
    }

    return `${selectedOptions.length} region(s) selected (${uniqueGroupIds.size} groups)`;
  }, [
    type,
    selectedOptions,
    allGroups,
    supportersRegions,
    supportersCountries,
  ]);

  const searchOptions = useMemo(() => {
    if (type === "group") {
      return [
        {
          label: "Groups",
          options: formattedGroups.filter(
            (option) => option.value && option.label
          ),
        },
      ];
    }

    if (type === "country") {
      return [
        {
          label: "Countries",
          options: supportersCountries.filter(
            (option) => option.value && option.label
          ),
        },
      ].filter((group) => group.options.length > 0);
    }

    return [
      {
        label: "Regions",
        options: supportersRegions.filter(
          (option) => option.value && option.label
        ),
      },
    ].filter((group) => group.options.length > 0);
  }, [type, formattedGroups, supportersRegions, supportersCountries]);

  const defaultLabel =
    type === "region"
      ? "Select Regions"
      : type === "country"
        ? "Select Countries"
        : "Select Groups";
  const defaultPlaceholder =
    type === "region"
      ? "Search by region (e.g. North America)..."
      : type === "country"
        ? "Search by country (e.g. United States)..."
        : "Search by group name...";

  return (
    <div className="group-audience-multi-select">
      <Search
        options={searchOptions}
        selectedOptions={selectedOptionsForSearch}
        onSelectedOptionsChange={handleSearchChange}
        label={label || defaultLabel}
        placeholder={placeholder || defaultPlaceholder}
        count={count}
      />
    </div>
  );
};

export default GroupMultiSelect;
